import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { toast } from 'sonner';
import { FileJson, Download, Upload, Loader2, Archive, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCLUDE_OPTS = [
  ['transcript', 'Transcript'],
  ['grading', 'Grading report'],
  ['disc', 'DISC / caller insight'],
  ['playbook', 'Callback playbook'],
  ['crm_context', 'CRM context'],
];

export default function AdminVconPage() {
  const [domains, setDomains] = useState([]);
  const [domainId, setDomainId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [include, setInclude] = useState({ transcript: true, grading: true, disc: true, playbook: true, crm_context: true });
  const [audio, setAudio] = useState('none');
  const [redact, setRedact] = useState(false);
  const [signed, setSigned] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importDomainId, setImportDomainId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [archives, setArchives] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/domains`)
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data.domains || []);
        setDomains(list);
        if (list.length) { setDomainId(list[0].id); setImportDomainId(list[0].id); }
      })
      .catch(() => toast.error('Failed to load domains'));
  }, []);

  useEffect(() => {
    if (!domainId) return;
    axios.get(`${API}/vcon/archive`, { params: { domain_id: domainId, limit: 10 } })
      .then((r) => setArchives(r.data))
      .catch(() => setArchives(null));
  }, [domainId]);

  const downloadArchive = async (rec) => {
    try {
      const r = await axios.get(`${API}/vcon/archive/${rec.id}/download`, { responseType: 'blob', timeout: 60000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${rec.vcon_uuid}.vcon.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Archive download failed');
    }
  };

  const domName = (d) => {
    const c = d.company;
    return (c && typeof c === 'object' ? c.name : c) || d.yabbit_domain || d.id;
  };

  const runExport = async () => {
    const incList = Object.keys(include).filter((k) => include[k]);
    if (!incList.length) { toast.error('Pick at least one thing to include'); return; }
    setExporting(true);
    try {
      const body = {
        domain_id: domainId,
        include: incList,
        audio,
        redact,
        signed,
      };
      if (dateFrom) body.date_from = dateFrom;
      if (dateTo) body.date_to = dateTo;
      const r = await axios.post(`${API}/vcon/export`, body, { responseType: 'blob', timeout: 300000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vcon-export-${domainId.slice(0, 8)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${r.headers['x-vcon-count'] || ''} vCon(s)`);
    } catch (e) {
      const msg = e.response?.status === 404 ? 'No analyzed calls match that range' : 'Export failed';
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const runImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const body = Array.isArray(parsed)
        ? { domain_id: importDomainId, vcons: parsed }
        : { domain_id: importDomainId, vcon: parsed };
      const r = await axios.post(`${API}/vcon/import`, body, { timeout: 120000 });
      setImportResult(r.data);
      toast.success(`Imported ${r.data.imported.length}, skipped ${r.data.skipped.length}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed — is the file valid vCon JSON?');
    } finally {
      setImporting(false);
    }
  };

  const inputCls = 'w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500/50';

  return (
    <AdminLayout>
      <div className="max-w-3xl space-y-6" data-testid="admin-vcon-page">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileJson className="w-6 h-6 text-teal-400" /> vCon Export &amp; Import
          </h1>
          <p className="text-white/40 text-sm mt-1">
            IETF vCon (draft-ietf-vcon-vcon-core, syntax 0.4.0) — portable conversation containers for archival and interop.
          </p>
        </div>

        {/* Bulk export */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-4">
          <h2 className="text-white font-semibold flex items-center gap-2"><Download className="w-4 h-4 text-teal-400" /> Bulk Export</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-white/30 text-xs block mb-1">Domain</label>
              <select value={domainId} onChange={(e) => setDomainId(e.target.value)} className={inputCls} data-testid="vcon-export-domain">
                {domains.map((d) => <option key={d.id} value={d.id}>{domName(d)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-white/30 text-xs block mb-1">From (optional)</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} data-testid="vcon-export-from" />
            </div>
            <div>
              <label className="text-white/30 text-xs block mb-1">To (optional)</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} data-testid="vcon-export-to" />
            </div>
          </div>
          <div>
            <p className="text-white/30 text-xs mb-2">Include in each vCon</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {INCLUDE_OPTS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-white/70 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={include[key]}
                    onChange={(e) => setInclude((s) => ({ ...s, [key]: e.target.checked }))}
                    data-testid={`vcon-bulk-include-${key}`}
                    className="accent-teal-500"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <label className="flex items-center gap-2 text-sm text-amber-300/80 cursor-pointer select-none">
              <input type="checkbox" checked={redact} onChange={(e) => setRedact(e.target.checked)}
                data-testid="vcon-bulk-redact" className="accent-amber-500" />
              Redact PII (mask customer phone/name)
            </label>
            <label className="flex items-center gap-2 text-sm text-teal-300/80 cursor-pointer select-none">
              <input type="checkbox" checked={signed} onChange={(e) => setSigned(e.target.checked)}
                data-testid="vcon-bulk-signed" className="accent-teal-500" />
              <ShieldCheck className="w-3.5 h-3.5" /> Sign with JWS (tamper-evident)
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <div>
              <label className="text-white/30 text-xs block mb-1">Recording audio</label>
              <select value={audio} onChange={(e) => setAudio(e.target.value)} className={inputCls} data-testid="vcon-export-audio">
                <option value="none">No audio (fast, max 500 calls)</option>
                <option value="url">Reference URL + SHA-512 hash (max 100)</option>
                <option value="inline">Inline base64 audio (slow, max 50)</option>
              </select>
            </div>
            <button
              onClick={runExport}
              disabled={exporting || !domainId || Object.values(include).every((v) => !v)}
              data-testid="vcon-export-run-btn"
              className="flex items-center justify-center gap-2 px-4 py-2 bg-teal-600/30 hover:bg-teal-600/40 border border-teal-500/30 text-teal-300 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {exporting ? 'Building ZIP…' : 'Export ZIP of vCons'}
            </button>
          </div>
        </div>

        {/* Auto archive */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-3" data-testid="vcon-archive-panel">
          <h2 className="text-white font-semibold flex items-center gap-2"><Archive className="w-4 h-4 text-emerald-400" /> Auto Archive</h2>
          <p className="text-white/30 text-xs">
            Every non-demo call is automatically archived as a <span className="text-teal-300">signed vCon</span> in object storage
            when grading completes. Verify signatures with the public <code>/api/vcon/jwks</code>.
          </p>
          {archives === null ? (
            <p className="text-white/40 text-sm">No archive data for this domain yet.</p>
          ) : (
            <>
              <p className="text-white/60 text-sm" data-testid="vcon-archive-total">
                {archives.total} archived vCon{archives.total === 1 ? '' : 's'} for this domain
              </p>
              <div className="space-y-1">
                {archives.archives.map((rec) => (
                  <div key={rec.id} className="flex items-center justify-between bg-black/20 border border-white/5 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-white/80 text-xs truncate">{rec.subject || rec.call_id}</p>
                      <p className="text-white/30 text-[11px]">{new Date(rec.created_at).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane' })} AEST · {(rec.size / 1024).toFixed(1)} KB · signed</p>
                    </div>
                    <button
                      onClick={() => downloadArchive(rec)}
                      data-testid={`vcon-archive-dl-${rec.id}`}
                      className="shrink-0 ml-3 p-1.5 text-teal-300 hover:bg-teal-500/10 rounded-md transition-colors"
                      title="Download signed vCon"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Import */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-4">
          <h2 className="text-white font-semibold flex items-center gap-2"><Upload className="w-4 h-4 text-violet-400" /> Import vCons</h2>
          <p className="text-white/30 text-xs">
            Upload a <code>.vcon.json</code> file (single vCon object or a JSON array). Calls with a transcript and grading
            land as <span className="text-emerald-300">analyzed</span>; others land as <span className="text-amber-300">pending</span> for the grading pipeline. Duplicates are skipped.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <div>
              <label className="text-white/30 text-xs block mb-1">Target domain</label>
              <select value={importDomainId} onChange={(e) => setImportDomainId(e.target.value)} className={inputCls} data-testid="vcon-import-domain">
                {domains.map((d) => <option key={d.id} value={d.id}>{domName(d)}</option>)}
              </select>
            </div>
            <label
              className="flex items-center justify-center gap-2 px-4 py-2 bg-violet-600/30 hover:bg-violet-600/40 border border-violet-500/30 text-violet-300 text-sm font-medium rounded-lg transition-colors cursor-pointer"
              data-testid="vcon-import-file-label"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {importing ? 'Importing…' : 'Choose vCon file…'}
              <input
                type="file"
                accept=".json,application/json"
                className="hidden"
                disabled={importing || !importDomainId}
                data-testid="vcon-import-file"
                onChange={(e) => { runImport(e.target.files?.[0]); e.target.value = ''; }}
              />
            </label>
          </div>
          {importResult && (
            <div className="bg-black/20 border border-white/10 rounded-lg p-3 text-sm" data-testid="vcon-import-result">
              <p className="text-emerald-300">Imported: {importResult.imported.length}</p>
              {importResult.imported.slice(0, 5).map((r) => (
                <p key={r.call_id} className="text-white/50 text-xs ml-3">· {r.call_id} → {r.status}</p>
              ))}
              {importResult.skipped.length > 0 && <p className="text-amber-300 mt-1">Skipped (duplicates): {importResult.skipped.length}</p>}
              {importResult.errors.length > 0 && (
                <p className="text-red-300 mt-1">Errors: {importResult.errors.map((e) => `#${e.index} ${e.error}`).join('; ')}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
